import { useTheme } from "@mui/material/styles";
import { Backdrop, Container, Grow, Modal, Stack, styled } from "@mui/material";
import { AccountDetailInfo } from "../../../_app/types";
import CustomPaper from "../../../_components/CustomPaper";
import Text from "../../../_components/Text";
import { useAppNavigate } from "../../../_hooks/use-app-navigate";
import { useDispatch, useSelector } from "react-redux";
import { useApi } from "../../../_hooks/use-api";
import { RootState } from "../../../store/store";
import MyButton from "../../../_components/MyButton";
import { useTranslation } from "react-i18next";
import { setAccounts, switchAccount } from "../../../store/slices/userSlice";
import { showNotification } from "../../../store/slices/notificationSlice";
import { HubError } from "../../../class/Error";
import { FC, useState } from "react";

interface DisconnectAccountPopupProps {
  open: boolean;
  onClose: (value: boolean) => void;
  account: AccountDetailInfo;
}

const CustomBackdrop = styled(Backdrop)(({ theme }) => ({
  backdropFilter: "blur(6px)",
  backgroundColor: "rgba(0, 0, 0, 0.5)",
}));

const DisconnectAccountPopup: FC<DisconnectAccountPopupProps> = ({ open, onClose, account }) => {
  const theme = useTheme();
  const handleClose = () => onClose(false);
  const { t } = useTranslation("account");
  const navigate = useAppNavigate();
  const dispatch = useDispatch();
  const api = useApi();
  const user = useSelector((state: RootState) => state.user);

  const [isLoadingDisconnectButton, setIsLoadingDisconnectButton] = useState<boolean>(false);

  const deleteAccount = async () => {
    setIsLoadingDisconnectButton(true);

    try {
      const newActiveAccount = await api.deleteAccount(account.id);

      const isActiveAccount = account.id === user.activeAccount.id;
      const isLastAccount = user.profile.accounts.length === 1;

      if (isActiveAccount) {
        if (isLastAccount) {
          dispatch(
            switchAccount({
              id: "",
              name: "",
              market: "",
              marginType: "",
              leverage: 0,
              linkDateTime: null,
            })
          );
        } else {
          dispatch(switchAccount(newActiveAccount));
        }
      }

      dispatch(setAccounts(user.profile.accounts.filter((item) => item.id !== account.id)));

      dispatch(
        showNotification({
          message: `Account ${account.name} was disconnected`,
          severity: "info",
        })
      );

      navigate.bots();
    } catch (error: any) {
      error instanceof HubError
        ? dispatch(
            showNotification({
              message: `${error.message} ${error.detailedMessage}`,
              severity: "error",
            })
          )
        : dispatch(showNotification({ message: error.message, severity: "error" }));
    } finally {
      setIsLoadingDisconnectButton(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} slots={{ backdrop: CustomBackdrop }}>
      <Grow in={open}>
        <Container
          className={"NoOpacity"}
          sx={{
            position: "fixed",
            top: "40%",
            left: 0,
            zIndex: 100,
            maxWidth: "100% !important",
            width: "100%",
            paddingLeft: "20px !important",
            paddingRight: "20px !important",
          }}
        >
          <CustomPaper sx={{ borderRadius: 2, paddingTop: 2 }} color={theme.palette.grey[300]}>
            <Stack direction={"column"} gap="24px">
              <Text textAlign="center" variant="h3">
                {t("sureDisconnect")}
              </Text>

              <MyButton
                variant="danger"
                onClick={deleteAccount}
                isLoading={isLoadingDisconnectButton}
              >
                {t("disconnectAccount")}
              </MyButton>
            </Stack>
          </CustomPaper>
        </Container>
      </Grow>
    </Modal>
  );
};

export default DisconnectAccountPopup;
